// SAST date helpers. KF49 operates on SAST (UTC+2, no DST). A bare
// new Date().toISOString().slice(0,10) returns the UTC calendar date,
// which lags into the PREVIOUS day during 00:00–02:00 SAST. Shift +2h
// before slicing so "today" is the SAST today. Council T3.11.
//
// Before this helper existed, ~6 places open-coded the +2h-then-slice
// shift and ~15 others got it wrong with a bare slice. Use these
// everywhere a YYYY-MM-DD "today" (or "this date in SAST") is needed for
// comparison, default form values, or storage path keys.

export const SAST_OFFSET_MS = 2 * 60 * 60 * 1000

/** YYYY-MM-DD for the current SAST calendar day. */
export function sastTodayYMD(): string {
  return new Date(Date.now() + SAST_OFFSET_MS).toISOString().slice(0, 10)
}

/** YYYY-MM-DD for an arbitrary instant, in SAST.
 *
 *  Throws a RangeError that NAMES this helper on an Invalid Date. It always
 *  threw — `toISOString()` on NaN is a bare "Invalid time value" — but that
 *  message names nothing, and the one that reached Sentry (KF49-PACKHOUSE-BM/
 *  -BN, RateTimeline on `new Date('infinity')`) took a day to place. The
 *  contract for a VALID instant is unchanged; 57 callers rely on a string
 *  back, so this does not go `| null`. A caller holding a value that MAY be
 *  unparsable (a PostgREST timestamptz, a cleared date input) checks first:
 *  `finiteDate()` below, or `isYmd()` for a YMD. */
export function sastYMD(at: number | Date = Date.now()): string {
  const ms = at instanceof Date ? at.getTime() : at
  if (!Number.isFinite(ms)) throw new RangeError(`sastYMD: not a valid instant (${String(at)})`)
  return new Date(ms + SAST_OFFSET_MS).toISOString().slice(0, 10)
}

/**
 * A `timestamptz` read back through PostgREST, as a Date — or null when it
 * holds no finite instant.
 *
 * Postgres's `'infinity'::timestamptz` (what the rate RPCs write for an
 * open-ended `effective_to`) serialises as the STRING "infinity", and
 * `new Date('infinity')` is an Invalid Date: the first `.toISOString()` on it
 * throws a RangeError that takes the panel's error boundary with it (Sentry
 * KF49-PACKHOUSE-BM / -BN, 2026-09-09). So: null, "", "infinity",
 * "-infinity" and anything else Date cannot parse all come back null, and
 * the caller renders the open/unknown branch it already has for null.
 */
export function finiteDate(v: string | number | Date | null | undefined): Date | null {
  if (v == null || v === '') return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isFinite(d.getTime()) ? d : null
}

/**
 * Is this a usable `YYYY-MM-DD`?
 *
 * `<input type="date">` yields `''` when the user CLEARS the field — not
 * undefined, not the previous value — and a persisted filter then stores the
 * blank. Everything below that builds a Date from a YMD has to survive that,
 * because `new Date('' + 'T00:00:00Z')` is an Invalid Date and the first
 * `.toISOString()` on it throws a RangeError that takes the screen's error
 * boundary with it. Check before you compute; guard the caller too, since a
 * report with no date has nothing to show either way.
 */
export function isYmd(v: string | null | undefined): v is string {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false
  return !Number.isNaN(new Date(v + 'T00:00:00Z').getTime())
}

/**
 * A naive wall-clock string — `YYYY-MM-DDTHH:mm` (or with `:ss`), exactly what
 * `<input type="datetime-local">` yields — as the instant it NAMES IN SAST.
 *
 * `new Date('2026-09-02T07:00')` parses that string in the BROWSER's zone, so
 * the same typed time becomes a different instant on a laptop set to UTC or to
 * Europe/London. For anything that lands on a pay day (a bulk clock, an
 * attested scan) that is not a display nicety: the recorded instant's SAST
 * calendar date is the `work_date`, and therefore the pay period. Ten minutes
 * of browser-timezone drift can post a 07:00 clock-in to the previous day.
 *
 * Returns null on a string that isn't a wall-clock, so a caller can refuse
 * rather than silently record `Invalid Date`.
 */
export function sastNaiveToISO(naive: string | null | undefined): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?$/.exec((naive ?? '').trim())
  if (!m) return null
  // SAST is UTC+2 with no DST, so a fixed offset is exact — no zone database
  // and no ambiguity window, which is why every helper in this file uses it.
  const at = new Date(`${m[1]}T${m[2]}:${m[3] ?? '00'}+02:00`)
  return Number.isNaN(at.getTime()) ? null : at.toISOString()
}

/**
 * The inverse of sastNaiveToISO: a stored instant as the wall clock a
 * `<input type="datetime-local">` shows — `YYYY-MM-DDTHH:mm`, in SAST whatever
 * the browser's zone. Feeding the input `new Date(iso)` fields instead would
 * show a Europe/London laptop a handover two hours early, and saving it back
 * would move the stored instant by the same two hours.
 *
 * Returns '' for a null, blank or unparseable value — what a cleared input
 * holds — so a caller can bind the result straight to `value`.
 */
export function sastISOToNaive(iso: string | null | undefined): string {
  if (!iso) return ''
  const ms = new Date(iso).getTime()
  if (Number.isNaN(ms)) return ''
  return new Date(ms + SAST_OFFSET_MS).toISOString().slice(0, 16)
}

/** HH:MM (24h) clock time for a timestamptz, in SAST. Returns null for a
 *  null/blank input so callers can render an em-dash. Used on printed
 *  attendance/clock documents where the punch time must read in local
 *  (Africa/Johannesburg) time regardless of the viewer's browser TZ. */
export function sastHM(iso: string | number | null | undefined): string | null {
  if (iso == null || iso === '') return null
  const ms = new Date(iso).getTime()
  if (isNaN(ms)) return null
  return new Date(ms + SAST_OFFSET_MS).toISOString().slice(11, 16)
}

/** HH:MM:SS in SAST — the walk replay's scrub clock, where the second is the
 *  point. Takes epoch ms as well as ISO, like sastHM. */
export function sastHMS(iso: string | number | null | undefined): string | null {
  if (iso == null || iso === '') return null
  const ms = new Date(iso).getTime()
  if (isNaN(ms)) return null
  return new Date(ms + SAST_OFFSET_MS).toISOString().slice(11, 19)
}

/**
 * YYYY-MM-DD one calendar day before the given ISO date.
 * Used to close an effective-dated history row's `effective_to` at
 * `today − 1` so it doesn't overlap the new row's `effective_from = today`
 * (the non-overlap invariant relied on by product_cost_at / *_at SUMs).
 * Matches the convention in UploadCostings and migration 039.
 */
export function isoMinusOne(iso: string): string {
  if (!isYmd(iso)) return iso   // never crash on a blank — see isYmd
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

/**
 * ISO-8601 week number (1–53) for a Date. The packhouse "pack week" — the
 * default when creating a weekly pack instruction. (Report pages that fork
 * their own {year, week}-shaped variants should migrate to ymdWeek below
 * when touched — REFACTOR-BACKLOG.)
 */
export function isoWeek(d: Date): number {
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return ymdWeek(ymd).week
}

/**
 * First day (YYYY-MM-01) of the current farm season, on/before SAST today.
 * Default season start = April (citrus); pass a farm_config
 * season_start_month override where one is loaded. Shared by the farm
 * report pages that default their From filter to season-to-date.
 */
export function seasonStartYMD(seasonStartMonth = 4): string {
  const today = sastTodayYMD()
  const y = Number(today.slice(0, 4))
  const m = Number(today.slice(5, 7))
  const startY = m >= seasonStartMonth ? y : y - 1
  return `${startY}-${String(seasonStartMonth).padStart(2, '0')}-01`
}

/**
 * "9 Jul 2026" display form for a date or timestamptz, en-ZA locale.
 * Bare YYYY-MM-DD inputs are pinned to UTC midnight so the calendar date
 * never shifts with the viewer's timezone. Null-safe (renders an em-dash).
 * The same 4-line helper was open-coded as `fmtDate` in ~45 module files —
 * import this instead of forking another copy.
 */
export function fmtDateZA(iso: string | null | undefined): string {
  if (!iso) return '—'
  // timeZone pinned to SAST: without it the RENDER resolves in the runtime's
  // timezone, so a viewer west of UTC (a US/Canada buyer opening a shared
  // compliance PDF) saw every date shifted one calendar day (L45). Bare dates
  // are UTC midnight = 02:00 SAST → same calendar day; timestamptz values
  // render as the SAST business day.
  return new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : '')).toLocaleDateString('en-ZA',
    { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' })
}

/**
 * "9 Jul" — the year-less companion to fmtDateZA, for places where the year is
 * already established by the surrounding context and the space is tight: a
 * chart's date axis, a compact ledger column, a "PHI until …" chip.
 *
 * It exists because those places were each open-coding their own, and drifting:
 * some rendered `day: '2-digit'` ("09 Jul"), none pinned the timezone, and one
 * of them (FarmCompliance) had grown into a whole local `fmtDate` fork. Same
 * two rules as fmtDateZA — bare YYYY-MM-DD pinned to UTC midnight so the
 * calendar date can't shift under a viewer, render pinned to SAST so it can't
 * shift under one either. Null-safe (em-dash).
 *
 * Use fmtDateZA whenever the year is NOT obvious from context — a year-less
 * date on a register spanning seasons is a date you can't act on.
 */
export function fmtDayMonthZA(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : '')).toLocaleDateString('en-ZA',
    { day: 'numeric', month: 'short', timeZone: 'Africa/Johannesburg' })
}

/**
 * "Jul 2026" — a month heading (a report grouped by month). Pinned like
 * fmtDateZA. The farm reports open-coded it without the SAST pin, so a viewer
 * west of UTC read July's picks under a "Jun 2026" heading (L77).
 */
export function fmtMonthYearZA(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : '')).toLocaleDateString('en-ZA',
    { month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' })
}

/**
 * "Thursday, 9 Jul" — a day heading on a feed whose days are bucketed in SAST
 * (`sastYMD`). Pinned to SAST so the printed day always matches the bucket.
 */
export function fmtWeekdayDayMonthZA(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : '')).toLocaleDateString('en-ZA',
    { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Africa/Johannesburg' })
}

/**
 * "9 Jul 2026, 14:05" display form for a timestamptz, en-ZA locale,
 * viewer-local time like fmtDateZA's timestamptz path. Null-safe (em-dash).
 * Was forked as `fmtDateTime` in the reject-dispatch print pages — import
 * this instead of forking another copy.
 */
export function fmtDateTimeZA(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** ISO-8601 week identity for a YYYY-MM-DD date, with its Monday–Sunday
 *  bounds. `year` is the ISO week-year, which differs from the calendar
 *  year around the Dec/Jan boundary (1 Jan 2027 → 2026-W53). `key` string-
 *  sorts chronologically. UTC-pinned like fmtDateZA, so the week never
 *  shifts with the viewer's timezone. */
export interface YmdWeek {
  key: string      // '2026-W30'
  year: number     // ISO week-year
  week: number     // 1–53
  startYMD: string // Monday, YYYY-MM-DD
  endYMD: string   // Sunday, YYYY-MM-DD
}

export function ymdWeek(ymd: string): YmdWeek {
  const d = new Date(ymd + 'T00:00:00Z')
  const dayNum = (d.getUTCDay() + 6) % 7           // Mon=0 … Sun=6
  const monday = new Date(d)
  monday.setUTCDate(monday.getUTCDate() - dayNum)
  const sunday = new Date(monday)
  sunday.setUTCDate(sunday.getUTCDate() + 6)
  d.setUTCDate(d.getUTCDate() - dayNum + 3)        // this week's Thursday
  const thursday = d.getTime()
  const year = d.getUTCFullYear()
  d.setUTCMonth(0, 1)
  if (d.getUTCDay() !== 4) d.setUTCMonth(0, 1 + ((4 - d.getUTCDay()) + 7) % 7)
  const week = 1 + Math.ceil((thursday - d.getTime()) / 604_800_000)
  return {
    key: `${year}-W${String(week).padStart(2, '0')}`,
    year,
    week,
    startYMD: monday.toISOString().slice(0, 10),
    endYMD: sunday.toISOString().slice(0, 10),
  }
}

/** Monday–Sunday range of a week for display, en-ZA, collapsing the parts
 *  the bounds share: '20–26 Jul 2026' · '27 Jul – 2 Aug 2026' ·
 *  '29 Dec 2025 – 4 Jan 2026'. */
export function weekRangeLabel(w: YmdWeek, locale = 'en-ZA'): string {
  // Day + year composed by hand: en-ZA zero-pads the day ('04 Jan') even
  // under day:'numeric', which reads wrong next to the unpadded start day.
  // `locale` only chooses the month's WORD ('Sept' / 'Sep.'); the digits and
  // their order are composed here and are the same in every language.
  const s = new Date(w.startYMD + 'T00:00:00Z')
  const e = new Date(w.endYMD + 'T00:00:00Z')
  const month = (d: Date) => d.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' })
  const end = `${e.getUTCDate()} ${month(e)} ${e.getUTCFullYear()}`
  if (s.getUTCMonth() === e.getUTCMonth() && s.getUTCFullYear() === e.getUTCFullYear())
    return `${s.getUTCDate()}–${end}`
  const start = s.getUTCFullYear() === e.getUTCFullYear()
    ? `${s.getUTCDate()} ${month(s)}`
    : `${s.getUTCDate()} ${month(s)} ${s.getUTCFullYear()}`
  return `${start} – ${end}`
}

/** The current ISO week and the (n−1) before it, newest first, labelled for
 *  a week picker: 'This week · 20–26 Jul 2026', 'Last week · …', then
 *  'Wk N · …'. `forward` prepends future weeks ('Next week', then 'Wk N') —
 *  a PLANNER surface looks ahead where a report looks back — most distant
 *  first, so the whole list reads as one descending timeline. `todayYMD` is
 *  injectable for tests. */
export interface WeekOption { week: YmdWeek; label: string }

/** The words a week list is labelled with. English unless a surface asks
 *  otherwise — the scouting summary's Afrikaans switch is the one caller that
 *  does, and it passes its own table rather than this module growing one. */
export interface WeekWords {
  thisWeek: string
  lastWeek: string
  nextWeek: string
  /** Prefix for a numbered week: 'Wk' → "Wk 37". */
  wk:       string
  locale:   string
}
export const ENGLISH_WEEK_WORDS: WeekWords = {
  thisWeek: 'This week', lastWeek: 'Last week', nextWeek: 'Next week', wk: 'Wk', locale: 'en-ZA',
}

export function recentWeekOptions(
  n = 12, todayYMD = sastTodayYMD(), forward = 0, words: WeekWords = ENGLISH_WEEK_WORDS,
): WeekOption[] {
  const out: WeekOption[] = []
  const thisMonday = ymdWeek(todayYMD).startYMD
  for (let i = forward; i >= 1; i--) {
    const w = ymdWeek(addDaysYmd(thisMonday, i * 7))
    const name = i === 1 ? words.nextWeek : `${words.wk} ${w.week}`
    out.push({ week: w, label: `${name} · ${weekRangeLabel(w, words.locale)}` })
  }
  let w = ymdWeek(todayYMD)
  for (let i = 0; i < n; i++) {
    const name = i === 0 ? words.thisWeek : i === 1 ? words.lastWeek : `${words.wk} ${w.week}`
    out.push({ week: w, label: `${name} · ${weekRangeLabel(w, words.locale)}` })
    w = ymdWeek(isoMinusOne(w.startYMD))
  }
  return out
}

export interface WeekGroup<T> { week: YmdWeek; rows: T[] }

/** Bucket rows into one group per ISO week, preserving row order within a
 *  group and emitting groups in first-encounter order — date-sorted input
 *  (asc or desc) comes out grouped in its own direction. A week can never
 *  emit twice, so unsorted input degrades to odd group ORDER, never to
 *  duplicate section keys. */
export function groupByWeek<T>(rows: T[], ymdOf: (r: T) => string): WeekGroup<T>[] {
  const byKey = new Map<string, WeekGroup<T>>()
  for (const r of rows) {
    const w = ymdWeek(ymdOf(r))
    let g = byKey.get(w.key)
    if (!g) byKey.set(w.key, g = { week: w, rows: [] })
    g.rows.push(r)
  }
  return [...byKey.values()]
}

/** YYYY-MM-DD arithmetic without timezone drift (dates in this app are
 *  calendar days, not instants). Hoisted here from the PHI advisor — the
 *  invoice period boundary needs the same day-shift. */
export function addDaysYmd(ymd: string, days: number): string {
  if (!isYmd(ymd)) return ymd   // never crash on a blank — see isYmd
  const d = new Date(ymd + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** `ymd` plus `months` calendar months, day-clamped: 31 Aug + 6 → 28 Feb, not
 *  3 Mar. A bare Date.UTC (or Date.setMonth) rolls a day the target month
 *  doesn't have into the NEXT month. Negative `months` goes back. */
export function addMonthsYmd(ymd: string, months: number): string {
  if (!isYmd(ymd)) return ymd   // never crash on a blank — see isYmd
  const [y, m, d] = ymd.split('-').map(Number)
  const target  = new Date(Date.UTC(y, m - 1 + months, 1))
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  return new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, lastDay)))
    .toISOString().slice(0, 10)
}
